import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { buildCourseSchema, AH_ORGANIZATION, SCOTT_PERSON } from '../config/schema'
import { assets } from '../config/assets'
import { functions } from '../lib/functions'
import { logGaEvent } from '../lib/analytics'
import {
  SmsConsentCheckbox,
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
} from '../components/forms/SmsConsentCheckbox'

// Course, not Product, and BOTH prices. The old schema declared only the $500
// two-month option while the page also sells the $2,500 year, which is a
// structured-data guidelines violation rather than merely an omission.
const HORSE_SENSE_COURSE_SCHEMA = buildCourseSchema({
  name: 'Horse Sense Coaching Class',
  description:
    'A live weekly horsemanship education class with Scott Purdum. Monday nights at 6pm EST. Learn how horses think, why they behave the way they do, and what to do about it. Includes recordings, personalized Q&A, and Rider\u2019s Way App access.',
  url: 'https://www.advantagehorsemanship.com/horse-sense',
  offers: [
    { name: 'Two months', price: '500.00' },
    { name: 'Full year', price: '2500.00' },
  ],
})

type Plan = 'bimonthly' | 'annual'

// Funnel instrumentation. Kept next to the plan type so a price change here is
// impossible to miss; a wrong `value` silently corrupts GA revenue reporting.
const HORSE_SENSE_PRICE: Record<Plan, number> = { bimonthly: 500, annual: 2500 }

function horseSenseItems(plan: Plan) {
  return [{
    // Same slug the purchase event reports (PRICE_TO_SLUG on the server).
    item_id: 'horse-sense-coaching',
    item_name: 'Horse Sense Coaching Class',
    item_variant: plan,
    price: HORSE_SENSE_PRICE[plan],
    quantity: 1,
  }]
}

interface HorseSenseLeadPayload {
  firstName: string
  email: string
  phone: string
  plan: Plan
  smsConsent?: boolean
  smsConsentText?: string
  smsConsentVersion?: string
}

interface CheckoutResult {
  url: string | null
}

const submitLead = httpsCallable<HorseSenseLeadPayload, CheckoutResult>(
  functions,
  'submitHorseSenseLead',
)

const CONTRAST_POINTS = [
  'The Accelerator works on you. Horse Sense teaches you to read and understand your horse.',
  'Confidence comes from sessions in the Accelerator. Knowledge comes from Horse Sense.',
  'Many members are in both. They work together. But they are not the same thing.',
]

const LEARNING_AREAS = [
  {
    number: '01',
    label: 'The Horse',
    body: 'How horses process information, respond to pressure, communicate discomfort, and build or lose trust. This is practical application you can use the next day, not theory.',
  },
  {
    number: '02',
    label: 'The Human',
    body: "What your body language, timing, and energy are actually communicating to your horse. Most riders are sending signals they don't know they're sending.",
  },
  {
    number: '03',
    label: 'The Connection',
    body: "Why some horse-human partnerships work and others don't. How to diagnose where your dynamic is breaking down and what to do about it.",
  },
  {
    number: '04',
    label: 'The Environment',
    body: "How location, other horses, weather, and situational pressure change your horse's behavior, and how to use that knowledge instead of fighting it.",
  },
]

const INCLUDED = [
  {
    title: 'Weekly Live Class',
    body: '60-minute live session every Monday at 6pm EST. Lecture, demonstration, or Q&A format depending on the week.',
  },
  {
    title: 'Join Any Week',
    body: 'No fixed start date. No catching up required. Drop in any Monday and pick up exactly where the class is.',
  },
  {
    title: 'Session Recordings',
    body: 'Every class recorded and available in your member portal within 24 hours.',
  },
  {
    title: 'Personalized Q&A',
    body: 'Bring your specific horse and situation. Scott answers questions live on every call.',
  },
  {
    title: 'Member Resource Library',
    body: 'Guides, frameworks, and reference materials available in your portal',
  },
  {
    title: "Rider's Way App",
    body: 'Full app access included free for one year with your membership.',
  },
]

const DIGITAL_PROGRAMS = [
  {
    title: 'Horse Behavior Foundations',
    body: 'A complete breakdown of how horses think, learn, and communicate. The foundation every rider needs before anything else makes sense.',
  },
  {
    title: 'Confident Rider System',
    body: 'A structured system for building rider confidence from the inside out. Addresses the mental and physical patterns that keep riders stuck.',
  },
]

const TESTIMONIALS = [
  {
    quote:
      'My gelding Rio would shut down every time I asked for collection. Scott showed me I was pulling when I thought I was asking. Two weeks of the homework he gave me and Rio started reaching into the contact on his own.',
    name: 'Karen Torres',
    location: 'Scottsdale, Arizona',
  },
  {
    quote:
      'I spent years thinking my horse was barn sour. Turns out I was the one creating the anxiety at the gate. Scott walked me through it on a coaching call and I could not believe the difference the next day.',
    name: 'Beth Marsden',
    location: 'Savannah, Georgia',
  },
  {
    quote:
      'My Paint mare Ginger went from refusing trailer loading to walking on calm in under a week. Scott identified the environment factor I was completely missing. Worth every penny of the annual.',
    name: 'Pam Hoffmann',
    location: 'Portland, Oregon',
  },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What is the difference between Horse Sense and the Confidence Accelerator?',
    a: 'The Accelerator is a live 12-week class that meets every Wednesday and focuses on building rider confidence. Horse Sense is a live weekly class that meets every Monday and focuses on the technical education of horse behavior. One works on you. The other teaches you to read and understand your horse. Many members are in both.',
  },
  {
    q: 'When do I get access to the digital programs?',
    a: 'Immediately after enrollment. Both programs are available in your member portal as soon as your payment processes.',
  },
  {
    q: 'What happens when my membership ends?',
    a: 'Your access to the digital programs and member portal closes at the end of your membership period. You can renew at any time.',
  },
  {
    q: 'Is the annual membership a one-time payment?',
    a: 'Yes. One payment of $2,500 gives you 12 months of full access to everything included in Horse Sense.',
  },
  {
    q: 'Can I upgrade from 2-month to annual?',
    a: 'Yes. Contact us and we will credit your original payment toward the annual membership.',
  },
]

type FormStatus = 'idle' | 'loading' | 'error'

function SmallCheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path
        d="M3 8l3.5 3.5L13 5"
        stroke="#C8A84B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const MEMBERSHIP_INCLUDES = [
  'All Four Factors coaching sessions',
  'Both digital programs included',
  'Personalized video feedback',
  "Rider's Way App access",
  'Member resource library',
]

export function HorseSensePage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('annual')
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  function openModal(plan: Plan) {
    setSelectedPlan(plan)
    setModalOpen(true)
    setStatus('idle')
    setErrorMessage('')
    // Step 2 of 4: they asked to buy. Page view is step 1, begin_checkout is
    // step 3, purchase is step 4. Without this step there is no way to tell a
    // page that fails to interest anyone from a form that scares people off.
    logGaEvent('select_item', {
      item_list_name: 'Horse Sense',
      items: horseSenseItems(plan),
    })
  }

  function closeModal() {
    setModalOpen(false)
    setFirstName('')
    setEmail('')
    setPhone('')
    setSmsConsent(false)
    setStatus('idle')
    setErrorMessage('')
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    setErrorMessage('')

    // Step 3 of 4: details entered, handing off to Stripe. Fired before the
    // await so a slow or failed callable still records the intent.
    logGaEvent('begin_checkout', {
      currency: 'USD',
      value: HORSE_SENSE_PRICE[selectedPlan],
      items: horseSenseItems(selectedPlan),
    })

    try {
      const result = await submitLead({
        firstName: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        plan: selectedPlan,
        // Consent only counts when they gave a number AND ticked the box.
        // Neither one alone is permission to text.
        smsConsent: smsConsent && phone.trim().length > 0,
        smsConsentText:
          smsConsent && phone.trim().length > 0 ? SMS_CONSENT_TEXT : undefined,
        smsConsentVersion:
          smsConsent && phone.trim().length > 0 ? SMS_CONSENT_VERSION : undefined,
      })
      const url = result.data.url
      if (url) {
        window.location.href = url
      }
    } catch {
      setStatus('error')
      setErrorMessage(
        'Something went wrong. Please try again or email support@advantagehorsemanship.com.',
      )
    }
  }

  function scrollToFramework() {
    document.getElementById('four-factors')?.scrollIntoView({ behavior: 'smooth' })
  }

  function scrollToMembership() {
    document.getElementById('membership')?.scrollIntoView({ behavior: 'smooth' })
  }

  const isDisabled = status === 'loading' || !firstName.trim() || !email.trim()

  return (
    <Layout>
      <ManagedSeo path="/horse-sense" jsonLd={[HORSE_SENSE_COURSE_SCHEMA, AH_ORGANIZATION, SCOTT_PERSON]} />
      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-navy overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${assets.scott.groundworkLiberty})` }}
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-4">
              Horse Sense Coaching Class
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-6">
              Finally Learn How to Read Your Horse
            </h1>
            <p className="font-body text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              Horse Sense is a live weekly coaching class that teaches you the
              technical side of horsemanship. Learn how horses think, why they
              behave the way they do, and what you can do about it. This is
              education, not just encouragement.
            </p>
            <div className="mb-10 max-w-2xl">
              <p className="font-body text-gold text-sm font-semibold">
                Every Monday at 6pm EST. Start this Monday.
              </p>
              <p className="font-body text-white/60 text-sm mt-2">
                New members can join any week. There is no fixed end date. This
                class runs continuously.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToMembership}
                style={{
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 36px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Reserve Your Spot
              </button>
              <button
                onClick={scrollToFramework}
                className="font-body text-sm font-medium text-white hover:text-gold transition-colors inline-flex items-center justify-center min-h-[52px] px-6 border border-white/30 hover:border-gold/50"
              >
                See What's Covered
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── POSITIONING ──────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-6">
              This Is Not a Confidence Class
            </h2>
            <p className="font-body text-base text-navy/70 leading-relaxed">
              The Confidence Accelerator is about getting your head right. Horse
              Sense is about understanding what is actually happening between
              you and your horse, technically, behaviorally, and practically.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {CONTRAST_POINTS.map((point, i) => (
              <div
                key={i}
                className="border-l-4 border-gold pl-5 py-2"
              >
                <p className="font-body text-base text-navy/80 leading-relaxed">
                  {point}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHAT YOU'LL LEARN ───────────────────────────────────────────── */}
      <section id="four-factors" className="bg-navy py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-white mb-4">
              What You&rsquo;ll Learn Inside Horse Sense
            </h2>
            <p className="font-body text-white/60 text-base max-w-2xl mx-auto leading-relaxed">
              Every class covers the technical education riders never got. Each
              session is a lecture, a demonstration, or a live Q&amp;A built
              around understanding horse behavior from the ground up.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {LEARNING_AREAS.map((area) => (
              <div
                key={area.label}
                className="p-8"
                style={{ backgroundColor: '#0D1F3C' }}
              >
                <div className="font-heading text-7xl text-gold/20 leading-none mb-4 select-none">
                  {area.number}
                </div>
                <h3 className="font-heading text-3xl text-white mb-3">
                  {area.label}
                </h3>
                <p className="font-body text-sm text-white/70 leading-relaxed">
                  {area.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHAT IS INCLUDED ─────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
              What You Get
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy">
              Everything Inside Horse Sense
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDED.map((item) => (
              <div
                key={item.title}
                className="border border-gray-100 border-t-2 pt-6 pb-6 px-6"
                style={{ borderTopColor: '#C8A84B' }}
              >
                <h3 className="font-heading text-2xl text-navy mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── THE TWO INCLUDED PROGRAMS ────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-4">
              Also Included With Your Membership
            </h2>
            <p className="font-body text-navy/60 text-base max-w-2xl mx-auto leading-relaxed">
              Your membership includes full access to both of these programs.
              They are digital and available immediately after enrollment.
            </p>
          </div>
          {/* Bundle product photo */}
          <div className="flex justify-center mb-10">
            <img
              src={assets.products.foundationBundle}
              alt="Two-guide bundle: Riders Foundation Guide and Groundwork Foundation Guide box mockups"
              width={700}
              height={450}
              loading="lazy"
              className="max-w-full h-auto"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {DIGITAL_PROGRAMS.map((program, idx) => (
              <div key={program.title} className="bg-white p-8 lg:p-10">
                <img
                  src={idx === 0 ? assets.products.ridersFoundation : assets.products.foundationGroundwork}
                  alt={`${program.title} box mockup`}
                  width={300}
                  height={380}
                  loading="lazy"
                  className="w-full max-w-[260px] h-auto mx-auto mb-6"
                />
                <div
                  className="w-12 h-1 mb-6"
                  style={{ backgroundColor: '#C8A84B' }}
                />
                <h3 className="font-heading text-3xl text-navy mb-4">
                  {program.title}
                </h3>
                <p className="font-body text-base text-navy/70 leading-relaxed">
                  {program.body}
                </p>
                <p className="font-body text-xs text-gold font-semibold tracking-wide uppercase mt-6">
                  Included with membership
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ──────────────────────────────────────────────────────── */}
      <section id="membership" className="bg-navy py-16 lg:py-24" style={{ scrollMarginTop: '96px' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
              Membership
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white">
              Choose Your Membership
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            {/* Card 1 -2-Month */}
            <div className="bg-black/40 border border-white/10 p-8 flex flex-col">
              <div className="mb-4 h-5">
                {/* spacer to align with "Best Value" label */}
              </div>
              <div className="mb-2">
                <span className="font-body text-6xl text-white font-bold">$500</span>
              </div>
              <p className="font-body text-white/50 text-sm mb-6">
                Two months of full access. Everything included.
              </p>
              <ul className="space-y-2 mb-8 flex-1">
                {MEMBERSHIP_INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <SmallCheckIcon />
                    <span className="font-body text-sm text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openModal('bimonthly')}
                style={{
                  backgroundColor: 'transparent',
                  color: '#C8A84B',
                  fontWeight: '700',
                  padding: '16px 24px',
                  fontSize: '15px',
                  border: '2px solid #C8A84B',
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Start 2-Month Membership
              </button>
            </div>

            {/* Card 2 -Annual */}
            <div className="bg-black/40 border border-white/10 p-8 flex flex-col">
              <div className="mb-4">
                <span className="font-body text-xs font-semibold text-gold tracking-widest uppercase">
                  Best Value
                </span>
              </div>
              <div className="mb-2">
                <span className="font-body text-6xl text-white font-bold">$2,500</span>
              </div>
              <p className="font-body text-white/50 text-sm mb-6">
                Full year of access. Maximum results.
              </p>
              <ul className="space-y-2 mb-8 flex-1">
                {MEMBERSHIP_INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <SmallCheckIcon />
                    <span className="font-body text-sm text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openModal('annual')}
                style={{
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 24px',
                  fontSize: '15px',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Start Annual Membership
              </button>
            </div>
          </div>

          <p className="font-body text-white/50 text-sm text-center">
            Access to both digital programs begins immediately after enrollment.
          </p>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            What Members Are Saying
          </h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="border border-gray-100 p-8">
                <div className="font-heading text-5xl text-gold/30 leading-none mb-4 select-none">
                  &ldquo;
                </div>
                <blockquote className="font-body text-base text-navy/80 leading-relaxed mb-6">
                  {t.quote}
                </blockquote>
                <p className="font-body text-sm font-semibold text-navy">
                  {t.name}
                </p>
                <p className="font-body text-xs text-navy/50">{t.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24" style={{ borderTop: '1px solid #f0f0f0' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            Common Questions
          </h2>
          <div className="divide-y divide-navy/10">
            {FAQS.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left py-5 flex items-start justify-between gap-4 min-h-[52px]"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-body text-base font-semibold text-navy">
                    {faq.q}
                  </span>
                  <span
                    className={`flex-shrink-0 text-gold mt-0.5 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-45' : ''
                    }`}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M10 4v12M4 10h12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="pb-5">
                    <p className="font-body text-sm text-navy/70 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA BANNER ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#C8A84B' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-5xl sm:text-6xl text-navy mb-4">
            Learn the Language Your Horse Is Already Speaking.
          </h2>
          <p className="font-body text-navy/70 text-lg mb-8">
            Every Monday at 6pm EST. Reserve your spot and start this week.
          </p>
          <button
            onClick={scrollToMembership}
            style={{
              backgroundColor: '#0A1628',
              color: '#FFFFFF',
              fontWeight: '700',
              padding: '18px 48px',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              minHeight: '52px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Reserve Your Spot
          </button>
          <p className="font-body text-xs text-navy/50 mt-4">
            Questions?{' '}
            <Link
              to="/contact"
              className="underline hover:text-navy transition-colors"
            >
              Contact us
            </Link>
          </p>
        </div>
      </section>

      {/* ─── LEAD CAPTURE MODAL ───────────────────────────────────────────── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/70 px-4 py-8"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="relative my-auto flex max-h-[calc(100dvh-4rem)] w-full max-w-md flex-col overflow-hidden bg-white">
            {/* Modal header */}
            <div className="flex-shrink-0 bg-navy px-8 py-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl text-white">
                  Start Horse Sense
                </h2>
                <p className="font-body text-xs text-gold mt-1">
                  {selectedPlan === 'annual'
                    ? 'Annual Membership, $2,500'
                    : '2-Month Membership, $500'}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-white/60 hover:text-white transition-colors ml-4 flex-shrink-0"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Modal form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-8">
              <p className="font-body text-sm text-navy/70 mb-6">
                Enter your details and you'll be taken directly to a secure
                checkout to complete your enrollment.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="hs-first-name"
                  >
                    First Name *
                  </label>
                  <input
                    id="hs-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="hs-email"
                  >
                    Email Address *
                  </label>
                  <input
                    id="hs-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="hs-phone"
                  >
                    Mobile Phone{' '}
                    <span className="text-navy/40 normal-case font-normal tracking-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="hs-phone"
                    name="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    maxLength={50}
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="(540) 555-0123"
                  />
                  <p className="font-body text-xs text-navy/50 mt-1 leading-relaxed">
                    Add your mobile number for class reminders by text. You can
                    enroll without it.
                  </p>
                </div>

                {/* A2P 10DLC: its own box, unchecked, never required. */}
                <div className="pt-2 border-t border-navy/10">
                  <SmsConsentCheckbox
                    id="hs-sms-consent"
                    checked={smsConsent}
                    onChange={setSmsConsent}
                  />
                </div>
              </div>

              {status === 'error' && errorMessage && (
                <p className="font-body text-sm text-red-600 mb-4">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? '#a08940' : '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 32px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {status === 'loading'
                  ? 'Redirecting to checkout...'
                  : 'Proceed to Secure Checkout →'}
              </button>

              <p className="font-body text-xs text-navy/40 mt-3 text-center">
                Powered by Stripe · Secure checkout
              </p>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}
